import { useCallback, useMemo } from 'react'
import { ArrowDown } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { CTASection } from '@/components/landing/CTASection'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import {
  CardTornado,
  FilterBar,
  HalftoneCover,
  ListingCard,
  ListingEmpty,
  TONES,
  halftoneLayers,
  monogram,
  optionsFrom,
  useListingFilters,
  type FilterGroup,
  type TornadoCard,
} from '@/components/landing/listing'
import { customerStories, type CustomerStory, type Metric, type StoryApp } from '@/data/customers'

const APP_ICON: Record<StoryApp, OfficeApp> = {
  Word: 'word',
  Excel: 'excel',
  PowerPoint: 'powerpoint',
}

/* ------------------------------------------------------------------------
   Tornado tiles: one per customer metric, built from data/customers.ts.
------------------------------------------------------------------------ */

function ResultTileFront({ story, metric }: { story: CustomerStory; metric: Metric }) {
  const tone = TONES[story.tone]
  const [fine, coarse] = halftoneLayers(tone.dot, 160)
  return (
    <div
      className="relative flex h-full w-full flex-col justify-between overflow-hidden p-[1em]"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      <div className="absolute inset-0" style={fine} />
      <div className="absolute inset-0" style={coarse} />
      <div className="relative flex items-center gap-[0.5em]">
        <span className="flex h-[1.9em] w-[1.9em] items-center justify-center bg-white">
          <OfficeAppIcon app={APP_ICON[story.officeApp]} className="h-[1.5em] w-[1.5em]" />
        </span>
        <span className="font-[family-name:var(--font-landing-subdisplay)] text-[0.7em] font-bold uppercase leading-tight tracking-[0.04em]">
          {story.documentType}
        </span>
      </div>
      <div className="relative">
        <p className="font-[family-name:var(--font-landing-subdisplay)] text-[1.75em] font-bold leading-[1.02] tracking-[-0.03em]">
          {metric.improvement}
        </p>
        <p className="mt-[0.45em] text-[0.72em] leading-[1.3] opacity-85">{metric.label}</p>
      </div>
      <p className="relative border-t border-current/30 pt-[0.6em] font-[family-name:var(--font-landing-subdisplay)] text-[0.72em] font-bold uppercase leading-tight tracking-[0.04em]">
        {story.company}
      </p>
    </div>
  )
}

function ResultTileBack({ story }: { story: CustomerStory }) {
  const tone = TONES[story.tone]
  const [fine, coarse] = halftoneLayers(tone.dot, 20)
  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      <div className="absolute inset-0" style={fine} />
      <div className="absolute inset-0" style={coarse} />
      <span className="relative font-[family-name:var(--font-landing-display)] text-[2.4em] leading-none opacity-80">
        {monogram(story.company)}
      </span>
    </div>
  )
}

/** Interleave stories so neighbouring tiles always belong to different customers. */
function buildTornadoCards(stories: CustomerStory[]): TornadoCard[] {
  const rounds = Math.max(...stories.map((s) => s.metrics.length))
  const cards: TornadoCard[] = []
  for (let m = 0; m < rounds; m++) {
    for (const story of stories) {
      const metric = story.metrics[m]
      if (!metric) continue
      cards.push({
        id: `${story.id}-${m}`,
        front: <ResultTileFront story={story} metric={metric} />,
        back: <ResultTileBack story={story} />,
      })
    }
  }
  return cards
}

/* ------------------------------------------------------------------------
   Listing
------------------------------------------------------------------------ */

/** Logo stand-in over the halftone tile: monogram box plus the company name. */
function StoryMark({ story }: { story: CustomerStory }) {
  const tone = TONES[story.tone]
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span
        className="flex h-20 w-20 items-center justify-center font-[family-name:var(--font-landing-display)] text-2xl leading-none"
        style={{ backgroundColor: tone.fg, color: tone.bg }}
      >
        {monogram(story.company)}
      </span>
      <span className="max-w-[12ch] font-[family-name:var(--font-landing-subdisplay)] text-2xl font-bold leading-[1.05] tracking-[-0.02em]">
        {story.company}
      </span>
    </div>
  )
}

const storyValues = (story: CustomerStory, groupId: string): string[] => {
  if (groupId === 'industry') return [story.industry]
  if (groupId === 'team') return [story.team]
  if (groupId === 'app') return [story.officeApp]
  return []
}

/* ------------------------------------------------------------------------
   Full stories
------------------------------------------------------------------------ */

function StoryDetail({ story }: { story: CustomerStory }) {
  const [fine, coarse] = halftoneLayers(TONES[story.tone].dot, 135)
  return (
    <article
      id={`story-${story.id}`}
      className="scroll-mt-28 grid gap-10 border-t border-[var(--color-landing-gray-200)] py-14 dark:border-[var(--color-landing-gray-800)] lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-16 lg:py-20"
    >
      <div className="flex flex-col gap-5 lg:sticky lg:top-28 lg:self-start">
        <span
          aria-hidden
          className="relative flex h-20 w-20 items-center justify-center overflow-hidden font-[family-name:var(--font-landing-display)] text-xl"
          style={{ backgroundColor: TONES[story.tone].bg, color: TONES[story.tone].fg }}
        >
          <span className="absolute inset-0" style={fine} />
          <span className="absolute inset-0" style={coarse} />
          <span className="relative">{monogram(story.company)}</span>
        </span>
        <h3 className="font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
          {story.company}
        </h3>
        <p className="text-sm text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
          {story.industry} · {story.employees} employees · {story.location}
        </p>
        <ul className="flex flex-wrap gap-1.5" aria-label="Templatean capabilities used">
          {story.solutions.map((solution) => (
            <li
              key={solution}
              className="bg-[var(--color-landing-primary)]/5 px-2 py-1 text-xs font-semibold text-[var(--color-landing-gray-700)] dark:bg-white/[0.07] dark:text-[var(--color-landing-gray-300)]"
            >
              {solution}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex min-w-0 flex-col gap-10">
        <p className="landing-text-display-sm text-[var(--color-landing-gray-900)] dark:text-white">{story.headline}</p>

        <dl className="grid gap-8 md:grid-cols-2">
          {[
            { term: 'The challenge', text: story.challenge },
            { term: 'What changed', text: story.solution },
          ].map((item) => (
            <div key={item.term}>
              <dt className="mb-2 text-sm font-bold uppercase tracking-[0.04em] text-[var(--color-landing-gray-900)] dark:text-white">
                {item.term}
              </dt>
              <dd className="landing-text-body-sm text-pretty text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                {item.text}
              </dd>
            </div>
          ))}
        </dl>

        <figure className="border-l border-[var(--color-landing-gray-300)] pl-6 dark:border-[var(--color-landing-gray-700)]">
          <blockquote className="landing-text-body-xl text-pretty text-[var(--color-landing-gray-900)] dark:text-white">
            &ldquo;{story.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-sm text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
            <span className="font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{story.author}</span>,{' '}
            {story.authorTitle}
          </figcaption>
        </figure>

        <div>
          <table className="w-full text-left text-[0.8125rem] tabular-nums sm:text-sm">
            <caption className="sr-only">{story.company} results, before and after Templatean</caption>
            <thead>
              <tr className="border-b border-[var(--color-landing-gray-900)] dark:border-white">
                <th scope="col" className="py-3 pr-2 font-bold uppercase sm:pr-4 tracking-[0.04em] text-[var(--color-landing-gray-900)] dark:text-white">
                  Metric
                </th>
                <th scope="col" className="px-2 py-3 font-bold uppercase sm:px-4 tracking-[0.04em] text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
                  Before
                </th>
                <th scope="col" className="px-2 py-3 font-bold uppercase sm:px-4 tracking-[0.04em] text-[var(--color-landing-gray-900)] dark:text-white">
                  After
                </th>
                <th scope="col" className="py-3 pl-2 text-right sm:pl-4 font-bold uppercase tracking-[0.04em] text-[var(--color-landing-accent)]">
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              {story.metrics.map((metric) => (
                <tr key={metric.label} className="border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-[var(--color-landing-gray-800)] dark:text-[var(--color-landing-gray-200)]">
                    {metric.label}
                  </th>
                  <td className="px-2 py-3 sm:px-4 text-[var(--color-landing-gray-900)]/65 line-through dark:text-white/65">
                    {metric.before}
                  </td>
                  <td className="px-2 py-3 sm:px-4 font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{metric.after}</td>
                  <td className="py-3 pl-2 text-right sm:pl-4 font-bold text-[var(--color-landing-accent)]">{metric.improvement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </article>
  )
}

/* ------------------------------------------------------------------------
   Page
------------------------------------------------------------------------ */

export default function CustomerStories() {
  const tornadoCards = useMemo(() => buildTornadoCards(customerStories), [])
  const getValues = useCallback(storyValues, [])
  const { selection, toggle, clear, filtered } = useListingFilters(customerStories, getValues)

  const groups: FilterGroup[] = useMemo(
    () => [
      { id: 'industry', label: 'Industry', options: optionsFrom(customerStories.map((s) => s.industry)) },
      { id: 'team', label: 'Team', options: optionsFrom(customerStories.map((s) => s.team)) },
      { id: 'app', label: 'Office app', options: optionsFrom(customerStories.map((s) => s.officeApp)) },
    ],
    []
  )

  return (
    <>
      <PageMeta
        title="Customer Stories - Real Results from Real Businesses | Templatean"
        description="See how companies like Greenfield Consulting, TechStart Solutions, and Summit Advisory Group transformed their document workflows with Templatean."
      />

      <LandingLayout>
        {/* Hero: small headline bottom-left, card tornado right (reference: works) */}
        <section className="relative -mt-[72px] overflow-hidden">
          <div className="landing-container grid lg:min-h-[100svh] lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-10">
            <div className="flex flex-col justify-end pb-6 pt-[calc(72px+3.5rem)] lg:pb-20">
              <HeroReveal delay={0.05}>
                <h1 className="landing-text-display-lg max-w-[16ch] text-[var(--color-landing-gray-900)] dark:text-white">
                  <span className="landing-highlight">Real results</span> from real businesses
                </h1>
              </HeroReveal>
              <HeroReveal delay={0.15}>
                <p className="landing-text-body-lg mt-6 text-pretty max-w-[42ch] text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
                  See how companies across industries are transforming their document workflows,
                  improving efficiency, and delivering better results with Templatean.
                </p>
              </HeroReveal>
              <HeroReveal delay={0.25}>
                <a href="#stories" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep mt-10">
                  See the stories
                  <ArrowDown className="h-5 w-5" />
                </a>
              </HeroReveal>
            </div>

            <CardTornado
              cards={tornadoCards}
              className="md:h-[42rem] lg:h-[100svh] lg:-mr-[clamp(1.5rem,2vw,2rem)]"
            />
          </div>
        </section>

        {/* Listing: filter bar + portrait card grid */}
        <section id="stories" className="landing-section scroll-mt-20 !pt-10 lg:!pt-16">
          <div className="landing-container">
            <div className="mx-auto border-t border-[var(--color-landing-gray-200)] pt-12 dark:border-[var(--color-landing-gray-800)] lg:max-w-[80%]">
              <h2 className="sr-only">All customer stories</h2>
              <FilterBar
                groups={groups}
                selection={selection}
                onToggle={toggle}
                onClear={clear}
                resultLabel={`${filtered.length} of ${customerStories.length} stories`}
              />

              {filtered.length > 0 ? (
                <StaggerContainer
                  amount={0.05}
                  className="mt-12 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {filtered.map((story) => (
                    <StaggerItem key={story.id}>
                      <ListingCard
                        href={`#story-${story.id}`}
                        cover={
                          <HalftoneCover tone={story.tone} aspect="4.39 / 5.88">
                            <StoryMark story={story} />
                          </HalftoneCover>
                        }
                        title={`${story.company}: ${story.headline}`}
                        tags={[
                          { label: story.industry, tone: 'blue' },
                          { label: story.team, tone: 'purple' },
                          { label: story.officeApp, tone: 'pink' },
                        ]}
                        excerpt={story.challenge}
                      />
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              ) : (
                <ListingEmpty
                  className="mt-12"
                  title="No stories match these filters"
                  description="Try another industry, team or Office app, or reset the filters to see every story."
                  onReset={clear}
                />
              )}
            </div>
          </div>
        </section>

        {/* Every story in full: challenge, change, quote and before/after numbers */}
        <section className="landing-section !pt-8">
          <div className="landing-container">
            <div className="mx-auto lg:max-w-[80%]">
              <TextReveal as="h2" className="landing-text-display-xl mb-14 text-[var(--color-landing-gray-900)] dark:text-white">
                The numbers, <span className="landing-highlight landing-highlight--secondary">in full.</span>
              </TextReveal>
              {customerStories.map((story) => (
                <ScrollReveal key={story.id} amount={0.05} direction="none">
                  <StoryDetail story={story} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <CTASection
          variant="accent"
          align="left"
          title="Ready to write your success story?"
          description="Join hundreds of companies transforming how they create documents. 14-day free trial, no credit card required."
          buttons={[
            { label: 'Start free trial', href: '/register', variant: 'primary' },
            { label: 'Book a demo', href: '/contact', variant: 'outline' },
          ]}
        />
      </LandingLayout>
    </>
  )
}
